/**
 * Kitchen tablet enrolment hooks (HMH-2417).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiConfig } from "../config/msalConfig";

export interface KitchenDevice {
  id: number;
  name: string;
  is_active: boolean;
  enrolled: boolean;
  code_pending: boolean;
  code_expires_at: string | null;
  created_at: string;
  created_by: string | null;
  enrolled_at: string | null;
  last_seen_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
}

interface KitchenDevicesResponse {
  devices: KitchenDevice[];
  legacy_ip_login: boolean;
}

export interface EnrolmentCodeResponse {
  device: KitchenDevice;
  enrolment_code: string;
  expires_in_minutes: number;
}

async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem("hmh_access_token");
  const response = await fetch(`${apiConfig.baseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

export function useKitchenDevices() {
  return useQuery<KitchenDevicesResponse>({
    queryKey: ["kitchen-devices"],
    queryFn: () => adminFetch<KitchenDevicesResponse>("/admin/kitchen-devices"),
  });
}

export function useCreateKitchenDevice() {
  const queryClient = useQueryClient();
  return useMutation<EnrolmentCodeResponse, Error, string>({
    mutationFn: (name) =>
      adminFetch<EnrolmentCodeResponse>("/admin/kitchen-devices", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["kitchen-devices"] }),
  });
}

export function useNewEnrolmentCode() {
  const queryClient = useQueryClient();
  return useMutation<EnrolmentCodeResponse, Error, number>({
    mutationFn: (id) =>
      adminFetch<EnrolmentCodeResponse>(`/admin/kitchen-devices/${id}/enrolment-code`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["kitchen-devices"] }),
  });
}

export function useRevokeKitchenDevice() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, number>({
    mutationFn: (id) => adminFetch(`/admin/kitchen-devices/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["kitchen-devices"] }),
  });
}

export function useSetLegacyIpLogin() {
  const queryClient = useQueryClient();
  return useMutation<{ legacy_ip_login: boolean }, Error, boolean>({
    mutationFn: (enabled) =>
      adminFetch("/admin/kitchen-auth-settings", {
        method: "PUT",
        body: JSON.stringify({ legacy_ip_login: enabled }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["kitchen-devices"] }),
  });
}
